import React, { useState } from 'react';
import { journalService } from '../services/adminService';

const DataImport = ({ onImportComplete }) => {
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    setProgress(0);
    setMessage('Reading file...');

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (!Array.isArray(data)) {
        throw new Error('File must contain an array of journal objects');
      }

      setMessage(`Found ${data.length} journals. Starting import...`);
      
      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < data.length; i++) {
        try {
          const journal = data[i];
          
          // Validate required fields
          if (!journal.title || !journal.authors || !journal.volume || !journal.issue) {
            console.warn(`Skipping journal ${i + 1}: Missing required fields`);
            errorCount++;
            continue;
          }

          // Transform data to match expected format
          const transformedJournal = {
            title: journal.title,
            authors: journal.authors,
            volume: journal.volume,
            issue: journal.issue,
            fileLink: journal.fileLink || '',
            isCurrentIssue: journal.isCurrentIssue || false,
            isArchived: journal.isArchived || false,
            date: journal.date || new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })
          };

          await journalService.addJournal(transformedJournal);
          successCount++;
          
          setProgress(((i + 1) / data.length) * 100);
          setMessage(`Imported ${successCount} journals, ${errorCount} errors...`);
          
        } catch (error) {
          console.error(`Error importing journal ${i + 1}:`, error);
          errorCount++;
        }
      }

      setMessage(`Import complete! Successfully imported ${successCount} journals. ${errorCount} errors.`);
      
      if (onImportComplete) {
        onImportComplete();
      }

    } catch (error) {
      console.error('Import error:', error);
      setMessage(`Import failed: ${error.message}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-lg font-semibold mb-4">Import Journal Data</h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload JSON File
          </label>
          <input
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            disabled={importing}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
          />
        </div>

        {importing && (
          <div className="space-y-2">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600">{Math.round(progress)}% complete</p>
          </div>
        )}

        {message && (
          <div className={`p-3 rounded-md text-sm ${
            message.includes('failed') || message.includes('error') 
              ? 'bg-red-100 text-red-700' 
              : 'bg-green-100 text-green-700'
          }`}>
            {message}
          </div>
        )}

        <div className="text-xs text-gray-500">
          <p><strong>Expected JSON format:</strong></p>
          <pre className="mt-1 bg-gray-100 p-2 rounded text-xs overflow-x-auto">
{`[
  {
    "title": "Journal Title",
    "authors": "Author Name",
    "volume": "Vol-1",
    "issue": "Issue 1",
    "fileLink": "https://example.com/file.pdf",
    "isCurrentIssue": true,
    "isArchived": false
  }
]`}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default DataImport; 